// Central content + config for the DSA eCommerce funnel landing page.
// Art direction mirrors poa.section3.ai: near-black + single accent, Outfit font.
// Copy reflects DSA's real model: DONE-FOR-YOU A-Z store management across
// Amazon, Walmart, eBay, Shopify & Temu. Client keeps 100% ownership.
// FTC note: no guarantees, no refund promises, results always disclaimed.

export const APPLY_URL = "https://apply.dsaecommerce.com/apply/";
export const ACCENT = "#2D5BFF";

export const ASSETS = {
  logo: "/assets/dsa-logo.webp",
  founder: "/assets/founder.webp",
  spokesperson: "/assets/spokesperson.webp",
  partnerBadges: "/assets/hero-asset.webp",
  brandsImage: "/assets/best-selling-brands.png",
  brandRows: [
    "/assets/brands/row1.png",
    "/assets/brands/row2.png",
    "/assets/brands/row3.png",
    "/assets/brands/row4.png",
    "/assets/brands/row5.png",
  ],
};

// Marketplace / platform brand marks served from /public/assets/platforms.
export const PLATFORMS = [
  { key: "amazon", name: "Amazon", src: "/assets/platforms/amazon.jpg", color: "#FF9900" },
  { key: "walmart", name: "Walmart", src: "/assets/platforms/walmart.png", color: "#0071DC" },
  { key: "ebay", name: "eBay", src: "/assets/platforms/ebay.png", color: "#E53238" },
  { key: "shopify", name: "Shopify", src: "/assets/platforms/shopify.png", color: "#7AB55C" },
  { key: "temu", name: "Temu", src: "/assets/platforms/temu.png", color: "#FB7701" },
];

// Top announcement bar (replaced the countdown timer).
export const SOCIALS = [
  { name: "Facebook", href: "https://facebook.com/profile.php?id=61560561179306", icon: "facebook" },
  { name: "Instagram", href: "https://www.instagram.com/dsa_ecommllc", icon: "instagram" },
  { name: "YouTube", href: "https://www.youtube.com/@DSAeCommerce", icon: "youtube" },
  { name: "LinkedIn", href: "https://linkedin.com/company/dsaecommerce", icon: "linkedin" },
];

export const LEGAL_LINKS = [
  { label: "Terms & Conditions", to: "/terms" },
  { label: "Privacy Policy", to: "/privacy" },
  { label: "Earnings Claims Statement", to: "/earnings-claims" },
];

// FTC-compliant results disclaimer reused across the page.
export const EARNINGS_DISCLAIMER =
  "Results shown are real, individual client outcomes and are not typical. DSA eCommerce does not promise any specific level of sales, income, or profit. Your results will vary based on effort, capital, market conditions, and other factors outside our control. See our Earnings Claims Statement.";

export const BAR_STATS = "9+ Years · 400+ Clients · $1.4B+ Managed";
export const BAR_CTA = "Q4 2026 intakes now open";

export const YOUTUBE_CHANNEL = "https://www.youtube.com/@DSAeCommerce";
export const YOUTUBE_CASE_STUDIES = [
  {
    id: "mw-4M-qLFzM",
    title: "$112K in first-year store sales",
    meta: "Grocery store · client since 2024",
  },
  {
    id: "Z4sIZUK8lIM",
    title: "3,000+ orders & $88K revenue in 12 months",
    meta: "0.4% defect rate · 99% valid tracking",
  },
];

export const HERO = {
  badge: "Official Amazon & Walmart Partner · eBay · Shopify · Temu",
  overline: "A–Z eCommerce management, done entirely for you.",
  subhead:
    "A leading store-management agency for Amazon, Walmart, eBay, Shopify & Temu. We build your branded store, source inventory at 30–45% below retail, launch everyday products, and run the entire operation — listings, ads, compliance and fulfillment. You keep 100% ownership and enjoy a stress-free, hands-off experience.",
  cta: "Book My Free Strategy Call",
  microcopy: "100% free · No obligation · Results vary; no income promised",
  trust: [
    "Official Amazon SPN Partner",
    "Walmart Solution Provider",
    "9+ Years · 400+ Clients",
  ],
};

export const STATS = [
  { value: 9, suffix: "+", label: "Years in business" },
  { value: 400, suffix: "+", label: "Clients served & counting" },
  { value: 1.4, prefix: "$", suffix: "B+", decimals: 1, label: "Revenue managed for our clients" },
  { value: 5, suffix: "", label: "Platforms: Amazon, Walmart, eBay, Shopify & Temu" },
];

export const PARTNER_TAGS = [
  "Official Amazon SPN Partner",
  "Official Walmart Solution Provider",
  "Amazon · Walmart · eBay · Shopify · Temu Stores",
  "9+ Years · 400+ Clients",
];

export const PANIC = {
  overline: "The do-it-yourself trap",
  lead: "\u201cI'll just figure it out myself.\u201d",
  paras: [
    "So you dove into Seller Central. Then came the listing rules, the ad dashboards, the supplier minimums, the compliance flags, the suspension emails at 2am. Weeks disappear and the needle barely moves.",
    "Meanwhile, the people actually profiting on Amazon, Walmart, eBay, Shopify and Temu aren't doing it alone — they have an operator running everything behind the scenes while they keep their time, their sanity, and 100% of their ownership.",
  ],
  closer: "Going it alone isn't a strategy. What wins is a proven team that runs the entire store for you.",
  statCard: {
    label: "One roof",
    value: "A–Z",
    body: "store setup, sourcing, listings, ads, compliance & fulfillment — all handled for you.",
  },
};

export const MISTAKES = [
  {
    n: "01",
    title: "You Don't Have The Time",
    sub: "An eCommerce store is a full-time operation.",
    body: "Between sourcing, listings, advertising, cases and compliance, running a store properly is a 40-hour week. Most owners can't give it that — so growth stalls before it starts.",
  },
  {
    n: "02",
    title: "Sourcing At Retail Kills Your Margin",
    sub: "No pricing power means no profit.",
    body: "We secure inventory 30–45% below retail from vetted U.S. manufacturers and distributors — the difference between a hobby and a real, profitable business.",
  },
  {
    n: "03",
    title: "Compliance & Suspension Landmines",
    sub: "One wrong move freezes everything.",
    body: "Amazon, Walmart, eBay, Shopify and Temu each change the rules constantly. A single misstep can suspend your account and lock your inventory overnight. We keep your store bulletproof.",
  },
  {
    n: "04",
    title: "Guessing At Listings & Ads",
    sub: "Amateur setups quietly bleed cash.",
    body: "Without a proven system, ad spend balloons and listings never rank. Nine years of experience is the difference between spending money and scaling it.",
  },
  {
    n: "05",
    title: "Chasing Fads Instead Of Everyday Demand",
    sub: "Trendy products die. Consumables don't.",
    body: "We build around household essentials people re-order every week — toothpaste, paper towels, cleaning supplies — for consistent, compounding demand.",
  },
];

export const COST = {
  overline: "The real cost of going it alone",
  heading: "Doing it yourself isn't free.",
  headingAccent: "It's the most expensive way to learn.",
  lineItems: [
    { label: "Months of your time learning the platforms", value: "-$18,000" },
    { label: "Inventory bought at full retail (no margin)", value: "-$24,000" },
    { label: "Wasted ad spend & compliance mistakes", value: "-$30,000+" },
  ],
  gainBox: {
    label: "You gain",
    value: "10–25%",
    sub: "target profit margins on a fully-managed, hands-off store — while you keep 100% ownership.",
  },
  dailyBox: { label: "Average DIY learning curve", value: "12", unit: " months" },
  punch: "A DIY store is a hobby. A professionally-run store is a machine.",
  question: "What is one more year of guesswork actually costing you?",
  cta: "Get A Store Built For You — Free Call",
};

export const CASE_STUDIES = [
  {
    tag: "Grocery & Consumables",
    icon: "cart",
    metricValue: "$112K",
    metricLabel: "First-year ordered product sales",
    sub: "An everyday grocery store scaled from launch to six figures in under 12 months.",
    span: "md:col-span-7",
  },
  {
    tag: "Toys & Games",
    icon: "toy",
    metricValue: "3,000+",
    metricLabel: "Orders shipped in 12 months",
    sub: "A seasonal toy catalog engineered into steady, year-round demand.",
    span: "md:col-span-5",
  },
  {
    tag: "Office Supplies",
    icon: "office",
    metricValue: "-38%",
    metricLabel: "Ad cost as a % of sales",
    sub: "Rebuilt campaigns for a B2B office-supply store — same spend, far more profit.",
    span: "md:col-span-5",
  },
  {
    tag: "Household Essentials",
    icon: "home",
    metricValue: "99%",
    metricLabel: "Valid tracking · 0.4% defect rate",
    sub: "A consumables store kept in flawless account health while scaling revenue.",
    span: "md:col-span-7",
  },
];

export const PATHS = [
  {
    name: "Do It Yourself",
    price: "Your nights & weekends",
    highlight: false,
    points: [
      { text: "A 40-hour-a-week second job", ok: false },
      { text: "Buy inventory at full retail — no margin", ok: false },
      { text: "One policy flag away from suspension", ok: false },
      { text: "Guess at listings, ads & compliance", ok: false },
    ],
  },
  {
    name: "Generic Agency",
    price: "Retainer + zero accountability",
    highlight: false,
    points: [
      { text: "Advice and reports — you still do the work", ok: false },
      { text: "You're one of 200 logos on their deck", ok: false },
      { text: "No official marketplace partner status", ok: false },
      { text: "No skin in the game on your results", ok: false },
    ],
  },
  {
    name: "DSA eCommerce",
    price: "Done-for-you across Amazon, Walmart, eBay, Shopify & Temu",
    highlight: true,
    points: [
      { text: "We build, stock & run the entire store", ok: true },
      { text: "Official Amazon & Walmart partner status", ok: true },
      { text: "Inventory sourced 30–45% below retail", ok: true },
      { text: "You keep 100% ownership & the profit", ok: true },
    ],
  },
];

export const PROCESS = [
  {
    step: "01",
    title: "Onboard Your Business",
    body: "We learn your goals, set up your branded store, and get everything compliant and approved from day one.",
  },
  {
    step: "02",
    title: "Secure Inventory",
    body: "We source in-demand, everyday products 30–45% below retail from vetted U.S. manufacturers and distributors.",
  },
  {
    step: "03",
    title: "Product Launch",
    body: "We build and optimize your listings, launch advertising, and take your store live across Amazon, Walmart, eBay, Shopify & Temu.",
  },
  {
    step: "04",
    title: "Grow & Profit",
    body: "We run the day-to-day and scale your store — you collect the profit, completely hands-off.",
  },
];

export const FAQS = [
  {
    q: "Who is DSA eCommerce best suited for?",
    a: "Established and aspiring sellers who want a professionally-run eCommerce store without the day-to-day operational burden. Results vary with market trends, account health and approvals, but many partners see their first sale within the first 90 days of going live.",
  },
  {
    q: "What services do you provide?",
    a: "Full A–Z store management: product research, listing creation, account management, compliance, advertising, and fulfillment coordination across Amazon, Walmart, eBay, Shopify and Temu. We build it, stock it, and run it for you.",
  },
  {
    q: "Do I keep ownership of my store and brand?",
    a: "Yes — you retain full ownership. We act as your strategic operations partner, never as a reseller or middleman. The store and brand are always yours.",
  },
  {
    q: "How long does it take to get started?",
    a: "Most stores are fully set up and selling within 60–90 days, depending on supplier approvals, compliance reviews, and product readiness.",
  },
  {
    q: "Do you promise or guarantee sales or profits?",
    a: "No. Marketplace performance depends on inventory, competition, capital, and platform policies — many of which are outside anyone's control. Many clients see strong results, but individual outcomes vary and are never promised. Please read our Earnings Claims Statement for full details.",
  },
  {
    q: "Are you really an official Amazon and Walmart partner?",
    a: "Yes. We're an official Amazon SPN Partner and Walmart Solution Provider, which gives us faster escalation paths and direct platform support for your store.",
  },
];
